import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencil,
  faTrash,
  faSpinner,
  faLocationDot,
  faUserPlus,
  faToggleOn,
  faToggleOff,
  faStar,
  faRightToBracket,
} from '@fortawesome/free-solid-svg-icons';
import {
  customerService,
  type CustomerLocation,
  type CustomerContact,
  type CreateLocationPayload,
  type CreateContactPayload,
} from '@/services/customer.service';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/shared/PhoneInput';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { usePermissions } from '@/hooks/use-permissions';
import { PortalAccessPanel } from './PortalAccessPanel';

// ── Location form ─────────────────────────────────────────────────────────────

function LocationForm({
  initial,
  onSubmit,
  onCancel,
  saving,
}: {
  initial?: Partial<CreateLocationPayload>;
  onSubmit: (d: CreateLocationPayload) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [f, setF] = useState<CreateLocationPayload>({
    name: initial?.name ?? '',
    addressLine1: initial?.addressLine1 ?? '',
    addressLine2: initial?.addressLine2 ?? '',
    city: initial?.city ?? '',
    state: initial?.state ?? '',
    country: initial?.country ?? '',
    postalCode: initial?.postalCode ?? '',
    phone: initial?.phone ?? '',
    alternatePhone: initial?.alternatePhone ?? '',
    email: initial?.email ?? '',
    amcDetails: initial?.amcDetails ?? '',
    notes: initial?.notes ?? '',
    isPrimary: initial?.isPrimary ?? false,
  });
  const [err, setErr] = useState<Record<string, string>>({});
  const set =
    (k: keyof CreateLocationPayload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!f.name.trim()) errs.name = 'Location name is required';
    if (!f.addressLine1?.trim()) errs.addressLine1 = 'Address Line 1 is required';
    if (Object.keys(errs).length) return setErr(errs);
    setErr({});
    // Strip empty optional strings to `undefined`. The backend DTO marks these
    // fields `@IsOptional()`, which only skips validation for null/undefined —
    // an empty string still hits validators such as `@IsEmail()` on `email`
    // and would fail the whole request (root cause: locations could not save).
    const clean = <T,>(v: T): T | undefined =>
      typeof v === 'string' && v.trim() === '' ? undefined : v;
    onSubmit({
      name: f.name.trim(),
      addressLine1: f.addressLine1.trim(),
      addressLine2: clean(f.addressLine2),
      city: clean(f.city),
      state: clean(f.state),
      country: clean(f.country),
      postalCode: clean(f.postalCode),
      phone: clean(f.phone),
      alternatePhone: clean(f.alternatePhone),
      email: clean(f.email?.trim().toLowerCase()),
      amcDetails: clean(f.amcDetails),
      notes: clean(f.notes),
      isPrimary: f.isPrimary,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Location Name" htmlFor="l-name" required error={err.name}>
          <Input id="l-name" value={f.name} onChange={set('name')} disabled={saving} />
        </FormField>
        <FormField label="Address Line 1" htmlFor="l-a1" required error={err.addressLine1}>
          <Input
            id="l-a1"
            value={f.addressLine1}
            onChange={set('addressLine1')}
            disabled={saving}
          />
        </FormField>
        <FormField label="Address Line 2" htmlFor="l-a2">
          <Input
            id="l-a2"
            value={f.addressLine2}
            onChange={set('addressLine2')}
            disabled={saving}
          />
        </FormField>
        <FormField label="City" htmlFor="l-city">
          <Input id="l-city" value={f.city} onChange={set('city')} disabled={saving} />
        </FormField>
        <FormField label="State" htmlFor="l-state">
          <Input id="l-state" value={f.state} onChange={set('state')} disabled={saving} />
        </FormField>
        <FormField label="Country" htmlFor="l-country">
          <Input id="l-country" value={f.country} onChange={set('country')} disabled={saving} />
        </FormField>
        <FormField label="Postal Code" htmlFor="l-pc">
          <Input id="l-pc" value={f.postalCode} onChange={set('postalCode')} disabled={saving} />
        </FormField>
        <FormField label="Phone" htmlFor="l-phone">
          <PhoneInput
            id="l-phone"
            value={f.phone ?? ''}
            onChange={(v) => setF((p) => ({ ...p, phone: v }))}
            disabled={saving}
          />
        </FormField>
        <FormField label="Alternate Phone" htmlFor="l-aphone">
          <PhoneInput
            id="l-aphone"
            value={f.alternatePhone ?? ''}
            onChange={(v) => setF((p) => ({ ...p, alternatePhone: v }))}
            disabled={saving}
          />
        </FormField>
        <FormField label="Email" htmlFor="l-email">
          <Input
            id="l-email"
            type="email"
            value={f.email}
            onChange={set('email')}
            disabled={saving}
          />
        </FormField>
      </div>
      <FormField label="AMC Details" htmlFor="l-amc">
        <Textarea
          id="l-amc"
          value={f.amcDetails}
          onChange={set('amcDetails')}
          rows={2}
          disabled={saving}
        />
      </FormField>
      <FormField label="Notes" htmlFor="l-notes">
        <Textarea id="l-notes" value={f.notes} onChange={set('notes')} rows={2} disabled={saving} />
      </FormField>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={f.isPrimary}
          onChange={(e) => setF((p) => ({ ...p, isPrimary: e.target.checked }))}
          disabled={saving}
        />
        Primary location
      </label>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save Location'}
        </Button>
      </div>
    </form>
  );
}

// ── Contact form ──────────────────────────────────────────────────────────────

function ContactForm({
  initial,
  onSubmit,
  onCancel,
  saving,
}: {
  initial?: Partial<CreateContactPayload>;
  onSubmit: (d: CreateContactPayload) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [f, setF] = useState<CreateContactPayload>({
    firstName: initial?.firstName ?? '',
    lastName: initial?.lastName ?? '',
    phone: initial?.phone ?? '',
    alternatePhone: initial?.alternatePhone ?? '',
    email: initial?.email ?? '',
    designation: initial?.designation ?? '',
    department: initial?.department ?? '',
    notes: initial?.notes ?? '',
    isPrimary: initial?.isPrimary ?? false,
  });
  const [err, setErr] = useState<Record<string, string>>({});
  const set =
    (k: keyof CreateContactPayload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!f.firstName.trim()) errs.firstName = 'First name is required';
    if (!f.lastName.trim()) errs.lastName = 'Last name is required';
    if (!f.phone.trim()) errs.phone = 'Phone is required';
    if (Object.keys(errs).length) return setErr(errs);
    setErr({});
    // Strip empty optional strings to `undefined` — an empty `email` would
    // otherwise fail the backend's `@IsEmail()` (see LocationForm submit).
    const clean = <T,>(v: T): T | undefined =>
      typeof v === 'string' && v.trim() === '' ? undefined : v;
    onSubmit({
      firstName: f.firstName.trim(),
      lastName: f.lastName.trim(),
      phone: f.phone.trim(),
      alternatePhone: clean(f.alternatePhone),
      email: clean(f.email?.trim().toLowerCase()),
      designation: clean(f.designation),
      department: clean(f.department),
      notes: clean(f.notes),
      isPrimary: f.isPrimary,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="First Name" htmlFor="ct-fn" required error={err.firstName}>
          <Input id="ct-fn" value={f.firstName} onChange={set('firstName')} disabled={saving} />
        </FormField>
        <FormField label="Last Name" htmlFor="ct-ln" required error={err.lastName}>
          <Input id="ct-ln" value={f.lastName} onChange={set('lastName')} disabled={saving} />
        </FormField>
        <FormField label="Phone" htmlFor="ct-phone" required error={err.phone}>
          <PhoneInput
            id="ct-phone"
            value={f.phone}
            onChange={(v) => setF((p) => ({ ...p, phone: v }))}
            disabled={saving}
          />
        </FormField>
        <FormField label="Alternate Phone" htmlFor="ct-aphone">
          <PhoneInput
            id="ct-aphone"
            value={f.alternatePhone ?? ''}
            onChange={(v) => setF((p) => ({ ...p, alternatePhone: v }))}
            disabled={saving}
          />
        </FormField>
        <FormField label="Email" htmlFor="ct-email">
          <Input
            id="ct-email"
            type="email"
            value={f.email}
            onChange={set('email')}
            disabled={saving}
          />
        </FormField>
        <FormField label="Designation" htmlFor="ct-desig">
          <Input
            id="ct-desig"
            value={f.designation}
            onChange={set('designation')}
            disabled={saving}
          />
        </FormField>
        <FormField label="Department" htmlFor="ct-dept">
          <Input id="ct-dept" value={f.department} onChange={set('department')} disabled={saving} />
        </FormField>
      </div>
      <FormField label="Notes" htmlFor="ct-notes">
        <Textarea
          id="ct-notes"
          value={f.notes}
          onChange={set('notes')}
          rows={2}
          disabled={saving}
        />
      </FormField>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={f.isPrimary}
          onChange={(e) => setF((p) => ({ ...p, isPrimary: e.target.checked }))}
          disabled={saving}
        />
        Primary contact
      </label>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save Contact'}
        </Button>
      </div>
    </form>
  );
}

// ── Contacts section (per location) ───────────────────────────────────────────

function ContactsSection({
  customerId,
  location,
  canEdit,
}: {
  customerId: string;
  location: CustomerLocation;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; contact?: CustomerContact } | null>(
    null,
  );

  const { data: contacts } = useQuery({
    queryKey: ['location-contacts', customerId, location.id],
    queryFn: () => customerService.listLocationContacts(customerId, location.id),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['location-contacts', customerId, location.id] });
    void qc.invalidateQueries({ queryKey: ['customer-stats', customerId] });
  };

  // B6.14 — portal management is a SEPARATE permission from contact editing. A user
  // who may correct a phone number must not thereby be able to mint a customer login,
  // so this is gated on its own permission rather than on `canEdit`.
  const { has } = usePermissions();
  const canManagePortal = has('customer-contact:portal:manage');
  const [portalContact, setPortalContact] = useState<CustomerContact | null>(null);

  const createM = useMutation({
    mutationFn: (d: CreateContactPayload) =>
      customerService.createContact(customerId, location.id, d),
    onSuccess: () => {
      toast.success('Contact added');
      setModal(null);
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to add contact')),
  });
  const updateM = useMutation({
    mutationFn: ({ id, d }: { id: string; d: CreateContactPayload }) =>
      customerService.updateContact(customerId, id, d),
    onSuccess: () => {
      toast.success('Contact updated');
      setModal(null);
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to update contact')),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => customerService.removeContact(customerId, id),
    onSuccess: () => {
      toast.success('Contact deleted');
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to delete contact')),
  });
  const toggleM = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      customerService.updateContact(customerId, id, { isActive }),
    onSuccess: (_d, v) => {
      toast.success(v.isActive ? 'Contact enabled' : 'Contact disabled');
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to update contact')),
  });

  return (
    <div className="mt-3 border-t border-border/60 pt-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Contacts ({contacts?.length ?? 0})
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={() => setModal({ mode: 'create' })}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
          >
            <FontAwesomeIcon icon={faUserPlus} className="h-3 w-3" /> Add Contact
          </button>
        )}
      </div>
      {contacts && contacts.length > 0 ? (
        <ul className="space-y-1.5">
          {contacts.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between rounded-md bg-muted/30 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 font-medium text-foreground">
                  {c.isPrimary && (
                    <FontAwesomeIcon icon={faStar} className="h-3 w-3 text-amber-500" />
                  )}
                  {c.firstName} {c.lastName}
                  {!c.isActive && (
                    <span className="rounded bg-muted px-1.5 text-[10px] text-muted-foreground">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {c.phone}
                  {c.designation ? ` · ${c.designation}` : ''}
                  {c.department ? ` · ${c.department}` : ''}
                  {c.email ? ` · ${c.email}` : ''}
                </div>
              </div>
              {(canEdit || canManagePortal) && (
                <div className="flex items-center gap-1">
                  {canManagePortal && (
                    <button
                      type="button"
                      onClick={() => setPortalContact(c)}
                      className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-primary/10 hover:text-primary"
                      title="Portal access"
                    >
                      <FontAwesomeIcon icon={faRightToBracket} className="h-3 w-3" />
                    </button>
                  )}
                  {canEdit && (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleM.mutate({ id: c.id, isActive: !c.isActive })}
                        className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                        title={c.isActive ? 'Disable' : 'Enable'}
                      >
                        <FontAwesomeIcon
                          icon={c.isActive ? faToggleOn : faToggleOff}
                          className="h-3.5 w-3.5"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => setModal({ mode: 'edit', contact: c })}
                        className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (await confirmDelete(`${c.firstName} ${c.lastName}`))
                            deleteM.mutate(c.id);
                        }}
                        className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">No contacts yet.</p>
      )}

      <Modal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={modal?.mode === 'edit' ? 'Edit Contact' : 'Add Contact'}
        size="lg"
      >
        {modal && (
          <ContactForm
            initial={
              modal.contact
                ? {
                    firstName: modal.contact.firstName,
                    lastName: modal.contact.lastName,
                    phone: modal.contact.phone ?? '',
                    alternatePhone: modal.contact.alternatePhone ?? '',
                    email: modal.contact.email ?? '',
                    designation: modal.contact.designation ?? '',
                    department: modal.contact.department ?? '',
                    notes: modal.contact.notes ?? '',
                    isPrimary: modal.contact.isPrimary,
                  }
                : undefined
            }
            saving={createM.isPending || updateM.isPending}
            onCancel={() => setModal(null)}
            onSubmit={(d) =>
              modal.mode === 'edit' && modal.contact
                ? updateM.mutate({ id: modal.contact.id, d })
                : createM.mutate(d)
            }
          />
        )}
      </Modal>

      <Modal
        open={portalContact !== null}
        onClose={() => setPortalContact(null)}
        title="Portal Access"
        size="lg"
      >
        {portalContact && (
          <PortalAccessPanel
            customerId={customerId}
            contactId={portalContact.id}
            contactName={`${portalContact.firstName} ${portalContact.lastName}`}
            contactEmail={portalContact.email ?? null}
          />
        )}
      </Modal>
    </div>
  );
}

// ── Location card ─────────────────────────────────────────────────────────────

function LocationCard({
  customerId,
  location,
  canEdit,
  onEdit,
  onDelete,
  onToggle,
}: {
  customerId: string;
  location: CustomerLocation;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-medium text-foreground">
            <FontAwesomeIcon icon={faLocationDot} className="h-3.5 w-3.5 text-primary" />
            {location.name}
            {location.isPrimary && (
              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                Primary
              </span>
            )}
            {!location.isActive && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                Inactive
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {[
              location.addressLine1,
              location.addressLine2,
              location.city,
              location.state,
              location.country,
              location.postalCode,
            ]
              .filter(Boolean)
              .join(', ')}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {[
              location.phone,
              location.email,
              location.amcDetails ? `AMC: ${location.amcDetails}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        {canEdit && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={onToggle}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
              title={location.isActive ? 'Disable' : 'Enable'}
            >
              <FontAwesomeIcon
                icon={location.isActive ? faToggleOn : faToggleOff}
                className="h-4 w-4"
              />
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
              title="Edit"
            >
              <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              title="Delete"
            >
              <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      <ContactsSection customerId={customerId} location={location} canEdit={canEdit} />
    </div>
  );
}

// ── Manager ───────────────────────────────────────────────────────────────────

export function LocationContactsManager({
  customerId,
  canEdit,
}: {
  customerId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{
    mode: 'create' | 'edit';
    location?: CustomerLocation;
  } | null>(null);

  const { data: locations, isLoading } = useQuery({
    queryKey: ['customer-locations', customerId],
    queryFn: () => customerService.listLocations(customerId),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['customer-locations', customerId] });
    void qc.invalidateQueries({ queryKey: ['customer-stats', customerId] });
  };

  const createM = useMutation({
    mutationFn: (d: CreateLocationPayload) => customerService.createLocation(customerId, d),
    onSuccess: () => {
      toast.success('Location added');
      setModal(null);
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to add location')),
  });
  const updateM = useMutation({
    mutationFn: ({ id, d }: { id: string; d: CreateLocationPayload }) =>
      customerService.updateLocation(customerId, id, d),
    onSuccess: () => {
      toast.success('Location updated');
      setModal(null);
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to update location')),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => customerService.removeLocation(customerId, id),
    onSuccess: () => {
      toast.success('Location deleted');
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to delete location')),
  });
  const toggleM = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      customerService.updateLocation(customerId, id, { isActive }),
    onSuccess: (_d, v) => {
      toast.success(v.isActive ? 'Location enabled' : 'Location disabled');
      invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to update location')),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Service Locations</h3>
        {canEdit && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setModal({ mode: 'create' })}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1.5 h-3 w-3" /> Add Location
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-5 w-5 animate-spin text-muted-foreground"
          />
        </div>
      ) : locations && locations.length > 0 ? (
        <div className="space-y-3">
          {locations.map((l) => (
            <LocationCard
              key={l.id}
              customerId={customerId}
              location={l}
              canEdit={canEdit}
              onEdit={() => setModal({ mode: 'edit', location: l })}
              onToggle={() => toggleM.mutate({ id: l.id, isActive: !l.isActive })}
              onDelete={async () => {
                if (await confirmDelete(l.name)) deleteM.mutate(l.id);
              }}
            />
          ))}
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
          No locations yet.{canEdit ? ' Add a location to attach contacts.' : ''}
        </p>
      )}

      <Modal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={modal?.mode === 'edit' ? 'Edit Location' : 'Add Location'}
        size="xl"
      >
        {modal && (
          <LocationForm
            initial={
              modal.location
                ? {
                    name: modal.location.name,
                    addressLine1: modal.location.addressLine1 ?? modal.location.address,
                    addressLine2: modal.location.addressLine2 ?? '',
                    city: modal.location.city ?? '',
                    state: modal.location.state ?? '',
                    country: modal.location.country ?? '',
                    postalCode: modal.location.postalCode ?? '',
                    phone: modal.location.phone ?? '',
                    alternatePhone: modal.location.alternatePhone ?? '',
                    email: modal.location.email ?? '',
                    amcDetails: modal.location.amcDetails ?? '',
                    notes: modal.location.notes ?? '',
                    isPrimary: modal.location.isPrimary,
                  }
                : undefined
            }
            saving={createM.isPending || updateM.isPending}
            onCancel={() => setModal(null)}
            onSubmit={(d) =>
              modal.mode === 'edit' && modal.location
                ? updateM.mutate({ id: modal.location.id, d })
                : createM.mutate(d)
            }
          />
        )}
      </Modal>
    </div>
  );
}
